import React from 'react'
import type { Coupon } from '../types'
import { Table, Tr, Th, Td } from '@/shared/ui/Table'
import { Badge, Button, Input, Tooltip } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { Copy } from 'lucide-react'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { MoreVertical, Search } from 'lucide-react'

export default function CouponTable({ items, onEdit, onDelete, affiliatesById }: { items: (Coupon & { is_live?: boolean })[]; onEdit: (c: Coupon) => void; onDelete: (c: Coupon) => void; affiliatesById?: Record<string, { name?: string|null; email?: string|null; code?: string|null }> }) {
  const { add: toast } = useToast()
  const [q, setQ] = React.useState('')
  const filtered = React.useMemo(() => {
    const s = q.trim().toLowerCase()
    if (!s) return items
    return items.filter(c => (
      c.name.toLowerCase().includes(s) || c.code.toLowerCase().includes(s)
    ))
  }, [items, q])

  function statusBadge(c: Coupon & { is_live?: boolean }) {
    const now = Date.now()
    const startMs = c.start_at ? new Date(c.start_at).getTime() : null
    const endMs = c.end_at ? new Date(c.end_at).getTime() : null
    const scheduled = (c.is_active !== false) && !!startMs && startMs > now
    const enabled = (typeof c.is_live === 'boolean')
      ? !!c.is_live
      : ((c.is_active !== false) && (!startMs || startMs <= now) && (!endMs || endMs >= now))
    const color: any = scheduled ? 'warning' : (enabled ? 'success' : 'error')
    const label = scheduled ? 'agendado' : (enabled ? 'ativo' : 'inativo')
    const tip = scheduled
      ? `Cupom agendado. Válido a partir de ${startMs ? new Date(startMs).toLocaleString('pt-BR') : '-'}.`
      : (enabled
          ? 'Cupom ativo. Aplicável agora e dentro da validade.'
          : `Cupom inativo (desligado ou fora da validade${endMs && endMs < now ? ' — expirado' : ''}).`)
    return (
      <Tooltip content={tip}>
        <Badge variant="soft" color={color}>{label}</Badge>
      </Tooltip>
    )
  }

  function discountLabel(c: Coupon) {
    if (c.discount_type === 'percent') return `${c.percent_off || 0}%`
    const value = (c.amount_off_cents || 0) / 100
    const curr = (c.currency || 'BRL').toUpperCase()
    try { return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: curr as any }).format(value) }
    catch { return `R$ ${value.toFixed(2).replace('.', ',')}` }
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
          <Input value={q} onChange={(e)=> setQ(e.target.value)} placeholder="Buscar por nome ou código" className="pl-9" />
        </div>
      </div>
      <Table>
        <thead>
          <Tr>
            <Th>Nome</Th>
            <Th>Código</Th>
            <Th>Desconto</Th>
            <Th>Afiliado</Th>
            <Th>Criação</Th>
            <Th>Vencimento</Th>
            <Th>Status</Th>
            <Th></Th>
          </Tr>
        </thead>
        <tbody>
          {filtered.map(c => (
            <Tr key={c.id}>
              <Td className="font-medium">{c.name}</Td>
              <Td>
                <div className="flex items-center gap-2">
                  <span className="font-mono">{c.code}</span>
                  <Tooltip content="Copiar código">
                    <Button
                      aria-label="Copiar código"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={async()=>{ try { await navigator.clipboard.writeText(c.code||''); toast({ title: 'Copiado', description: 'Código copiado para a área de transferência', variant: 'success', soft: true }) } catch { toast({ title: 'Falha ao copiar', variant: 'error', soft: true }) } }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </Tooltip>
                </div>
              </Td>
              <Td>{discountLabel(c)}</Td>
              <Td className="text-sm text-foreground/80">{(() => { const a = c.affiliate_user_id ? affiliatesById?.[c.affiliate_user_id] : null; return a ? (a.name || a.email || a.code || c.affiliate_user_id) : '—' })()}</Td>
              <Td>{c.created_at ? new Date(c.created_at).toLocaleDateString('pt-BR') : '-'}</Td>
              <Td>{c.end_at ? new Date(c.end_at).toLocaleDateString('pt-BR') : '—'}</Td>
              <Td>{statusBadge(c)}</Td>
              <Td className="text-right">
                <DropdownMenu
                  trigger={<Button variant="ghost" className="h-9 w-9 p-0"><MoreVertical className="h-4 w-4" /></Button>}
                  items={[
                    { label: 'Editar', onSelect: ()=> onEdit(c) },
                    { label: <span className="text-destructive">Excluir</span>, onSelect: ()=> onDelete(c) },
                  ]}
                />
              </Td>
            </Tr>
          ))}
          {filtered.length===0 && (
            <Tr><Td colSpan={7} className="text-center text-sm text-foreground/70 py-6">Nenhum cupom encontrado</Td></Tr>
          )}
        </tbody>
      </Table>
    </div>
  )
}
